import React, { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from 'flow/components/ui-components/Icon';
import Tooltip from 'flow/components/ui-components/Tooltip';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectFeedIsPinned } from 'flow/selectors/video-selectors';
import { togglePinFeedAction } from 'flow/actions/video-actions';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';

interface PinOverlayButtonProps {
    feedName: string;
}

/**
 * Small corner button layered above the full-tile fullscreen overlay; appears
 * on tile hover and stays visible while the feed is pinned. Self-contained:
 * reads its own pinned state and dispatches the toggle.
 */
const PinOverlayButton: React.FC<PinOverlayButtonProps> = ({ feedName }) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const feedIsPinned = useAppSelector((state) =>
        selectFeedIsPinned(state, feedName),
    );

    const togglePin = useCallback(() => {
        dispatch(togglePinFeedAction(feedName));
    }, [dispatch, feedName]);

    // Pinning is local — it only changes this user's own layout, not what other
    // participants see — so the label spells out "your view".
    const label = i18next.t(
        feedIsPinned
            ? 'videoconference.remote_video.unpin_feed'
            : 'videoconference.remote_video.pin_feed',
        { name: sliceUUIDFromUserName(feedName) },
    );

    // Positioning + hover-reveal live on the Tooltip's reference element (its
    // wrapper), so the tooltip anchors to the button's real position; the button
    // itself only carries shape/colour.
    return (
        <Tooltip
            label={label}
            placement="bottom"
            tooltipClassName={twMerge(
                'absolute end-2 top-2 z-[12] transition-opacity',
                feedIsPinned
                    ? 'opacity-100'
                    : 'opacity-0 group-focus-visible-within:opacity-100 group-hover:opacity-100',
            )}
        >
            <button
                type="button"
                className={twMerge(
                    'flex items-center justify-center rounded-full p-2 hover:cursor-pointer',
                    feedIsPinned
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-gray-600/80 text-white',
                )}
                onClick={togglePin}
                aria-label={label}
            >
                <Icon
                    iconKey={feedIsPinned ? 'keep_off' : 'push_pin'}
                    size="1.25rem"
                />
            </button>
        </Tooltip>
    );
};

export default PinOverlayButton;
